import type { PendingApproval } from '@/api/inspections';

/**
 * The rules the approval screen renders, kept out of the component.
 *
 * <p>
 * Same shape as `bookings/draft.ts`, `units/intake.ts` and `jobs/queue.ts`: the arithmetic and the
 * refusals are checkable without a browser, and the screen is left with layout. What is here is
 * everything a manager is told *before* they press a button — the server stays authoritative, and
 * if the two ever disagree this is the one that is wrong.
 * </p>
 */

/** What the queue is holding, said once above the list. */
export interface QueueSummary {
  count: number;
  /** Money recorded and deliberately not on any booking. The whole exposure of the backlog. */
  heldBack: number;
  longestWaitDays: number;
  /** How many the customer never agreed to. These are the ones that come back as disputes. */
  unacknowledged: number;
}

export function summarise(rows: readonly PendingApproval[]): QueueSummary {
  return {
    count: rows.length,
    heldBack: rows.reduce((sum, row) => sum + row.proposedCharge, 0),
    longestWaitDays: rows.reduce((longest, row) => Math.max(longest, row.daysWaiting), 0),
    unacknowledged: rows.filter((row) => !row.acknowledgedByCustomer).length,
  };
}

/**
 * How much of this charge the shop is not holding against.
 *
 * <p>
 * The second half of the decision, and the half a total alone hides: approving ₹18,000 against a
 * ₹9,750 deposit is not a deduction, it is an invoice somebody has to go and collect. Null means
 * the deposit covers it — including the case where no deposit was taken and nothing is covered,
 * which is deliberately *not* null: a booking with no deposit is the worst version of this, not the
 * exempt one.
 * </p>
 */
export function uncovered(row: PendingApproval): number | null {
  const held = row.depositHeld ?? 0;
  const gap = row.proposedCharge - held;

  return gap > 0 ? gap : null;
}

/**
 * Why this decision cannot be submitted yet, in the order a manager would fix them.
 *
 * <p>
 * Mirrors `ReturnInspectionService.ApproveAsync`. Refusing without saying why leaves the clerk who
 * quoted the figure with nothing to tell the customer, which is why the server refuses it and why
 * the button is disabled here rather than after the click.
 * </p>
 */
export function decisionProblems(approve: boolean, note: string): string[] {
  if (!approve && note.trim() === '') {
    return [
      'Say why it is being refused. Whoever quoted the figure has to tell the customer something.',
    ];
  }

  return [];
}

/**
 * How long this has been sitting, as words rather than a number.
 *
 * <p>
 * The wait is what makes a row urgent, and "3d" in a column reads as data while "waiting 3 days"
 * reads as a fact about a customer. Both are shown; this is the sentence.
 * </p>
 */
export function waitedFor(days: number): string {
  if (days <= 0) {
    return 'Waiting since today';
  }

  return days === 1 ? 'Waiting a day' : `Waiting ${days} days`;
}

/**
 * The tone the wait is drawn in.
 *
 * <p>
 * A week is the line, and it is not arbitrary: an unapproved charge holds up the deposit settlement
 * behind it, so a fortnight in this queue is a customer who has been waiting a fortnight for their
 * money with nothing to show them.
 * </p>
 */
export function waitTone(days: number): 'neutral' | 'warning' | 'negative' {
  if (days >= 14) {
    return 'negative';
  }

  return days >= 7 ? 'warning' : 'neutral';
}
